<script setup lang="ts">
import { computed } from 'vue';
import type { NotificationEntry, NotificationMessage } from './types';
import { info, warning, error, success } from '@/components/icons/icons';

export interface NotificationProps {
  value?: NotificationEntry | NotificationMessage;
  closable?: boolean;
}

const { value = undefined, closable = false } = defineProps<NotificationProps>();

defineEmits(['click:close']);

const iconMap = {
  info,
  warning,
  error,
  success,
} as const;

const icon = computed(() => {
  if (!value) return undefined;

  // If icon is explicitly set to false, don't show any icon
  if (value.icon === false) return undefined;

  // If icon is explicitly provided, use it
  if (value.icon) return value.icon;

  // Otherwise, use the default icon based on type
  return iconMap[value.type];
});

const hasIcon = computed(() => !!icon.value);
</script>

<template>
  <v-alert
    v-if="value"
    class="inline-alert"
    :class="{ 'no-icon': !hasIcon }"
    :closable="closable"
    :type="value.type"
    :title="value.title"
    :icon="icon || undefined"
    variant="tonal"
    @click:close="$emit('click:close', $event)"
  >
    <template v-if="icon" #prepend>
      <v-icon size="24">{{ icon }}</v-icon>
    </template>

    <template #text>
      {{ value.message }}

      <slot name="append" v-bind="{ notification: value }" />

      <p v-if="value.stackTrace" class="code">
        {{ value.stackTrace }}
      </p>
    </template>
  </v-alert>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;
@use '@/styles/variables' as *;

.inline-alert {
  display: inline-flex;
  width: fit-content;
  padding: 8px 16px 8px 8px;

  &.no-icon {
    padding: 16px;

    :deep(.v-alert__prepend) {
      display: none;
    }

    :deep(.v-alert-title) {
      margin-top: 0;
    }
  }

  &:not(.no-icon) {
    :deep(.v-alert__prepend) {
      align-self: flex-start;
      margin-right: 4px;

      i {
        font-size: 24px;
        height: 24px;
        width: 24px;
      }
    }
  }

  :deep(.v-alert-title) {
    @include typography('body-1');
    font-weight: $fontweight_bold !important;
    margin-top: 2px;
  }

  :deep(.v-alert__content) {
    font-weight: normal;
  }

  :deep(.v-alert__text) {
    margin-right: 16px;
  }

  :deep(.v-alert__append),
  :deep(.v-alert__close) {
    padding: 0;
    margin: 0;
  }
}
</style>
